/**
 * useNavItems — CHROME-1: which destinations live in the studio's
 * nav, and in what order.
 *
 * Both chromes read this one hook.  The rail and the Aperture Bar
 * are two drawings of the same list — if they read separate state
 * they'd disagree the moment a studio flipped between them.
 *
 * Storage shape (`orbit.nav_items`, JSON):
 *   [{ "id": "today", "visible": true }, …]
 *
 * SELF-HEALING, and this is the important part.  The stored list is
 * a *preference*, not a schema.  It was written by whatever version
 * of Orbit the studio last opened, so on every read we reconcile it
 * against the live `NAV_DESTINATIONS`:
 *
 *   • ids we no longer ship are dropped (a retired destination
 *     shouldn't render as an empty tab);
 *   • ids we ship but the stored list has never seen are APPENDED,
 *     visible.  This is the case that matters — ship a seventh
 *     destination next month and every existing studio sees it
 *     instead of silently missing it forever;
 *   • `required` destinations are forced visible regardless of what
 *     the stored value says (see NavDestination.required for why).
 *
 * Persistence + cross-tab sync follow useNavStyle / useSimplifiedNav
 * exactly: localStorage plus a manually-dispatched `storage` event
 * so subscribers in the SAME tab re-render without a refresh (the
 * native event only fires on other tabs).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  NAV_DESTINATIONS,
  NAV_DESTINATION_IDS,
  navDestinationById,
  type NavDestination,
} from '@/lib/navDestinations';

const STORAGE_KEY = 'orbit.nav_items';

export interface NavItemPref {
  id: string;
  visible: boolean;
}

/** The out-of-the-box nav: every destination, canonical order, all on. */
export function defaultNavItems(): NavItemPref[] {
  return NAV_DESTINATION_IDS.map((id) => ({ id, visible: true }));
}

/**
 * Reconcile a (possibly stale, possibly garbage) stored list against
 * what we actually ship today.  Pure — exported so the settings
 * editor and the onboarding picker can heal a list they're holding
 * without a round-trip through localStorage.
 */
export function healNavItems(stored: unknown): NavItemPref[] {
  const known = new Set(NAV_DESTINATION_IDS);
  const seen = new Set<string>();
  const healed: NavItemPref[] = [];

  if (Array.isArray(stored)) {
    for (const raw of stored) {
      if (!raw || typeof raw !== 'object') continue;
      const id = (raw as { id?: unknown }).id;
      if (typeof id !== 'string') continue;
      // Retired destination, or a duplicate id from a bad write.
      if (!known.has(id) || seen.has(id)) continue;
      seen.add(id);
      const dest = navDestinationById(id);
      healed.push({
        id,
        // Locked destinations ignore a stored `false` — a preference
        // file edited by hand (or written by an older build with a
        // different lock set) can't hide the escape hatch.
        visible: dest?.required ? true : (raw as { visible?: unknown }).visible !== false,
      });
    }
  }

  // Anything that shipped after this preference was last written
  // joins the end of the list, switched on.  New destinations
  // announce themselves; they don't hide.
  for (const id of NAV_DESTINATION_IDS) {
    if (seen.has(id)) continue;
    healed.push({ id, visible: true });
  }

  return healed;
}

function read(): NavItemPref[] {
  if (typeof window === 'undefined') return defaultNavItems();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return defaultNavItems();
    return healNavItems(JSON.parse(raw));
  } catch {
    // Unparseable / private mode — fall back to the full nav rather
    // than rendering an empty bar.
    return defaultNavItems();
  }
}

export interface UseNavItems {
  /** Healed preference list, in the studio's order. */
  items: NavItemPref[];
  /** Resolved + filtered destinations — what the chrome renders. */
  destinations: NavDestination[];
  /** Replace the whole list (used by drag-reorder). */
  setItems: (next: NavItemPref[]) => void;
  /** Flip one destination's visibility.  No-op on locked ones. */
  toggle: (id: string) => void;
  /** Nudge one destination up (-1) or down (+1) in the order. */
  move: (id: string, direction: -1 | 1) => void;
  /** Back to every destination, canonical order. */
  reset: () => void;
  /** True when the whole list is already the shipped default. */
  isDefault: boolean;
}

export function useNavItems(): UseNavItems {
  const [items, setItemsState] = useState<NavItemPref[]>(read);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      if (e.newValue === null) {
        setItemsState(defaultNavItems());
        return;
      }
      try {
        setItemsState(healNavItems(JSON.parse(e.newValue)));
      } catch {
        setItemsState(defaultNavItems());
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const persist = useCallback((next: NavItemPref[]) => {
    const healed = healNavItems(next);
    const serialized = JSON.stringify(healed);
    try {
      window.localStorage.setItem(STORAGE_KEY, serialized);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: STORAGE_KEY,
          newValue: serialized,
        }),
      );
    } catch {
      /* private mode — in-memory only for this session */
    }
    setItemsState(healed);
  }, []);

  // NOTE: these read `items` from the closure rather than using the
  // functional-updater form on purpose.  Persisting writes to
  // localStorage and dispatches an event; doing that inside a state
  // updater would fire it twice under StrictMode's double-invoke.
  const toggle = useCallback(
    (id: string) => {
      if (navDestinationById(id)?.required) return;
      persist(
        items.map((it) => (it.id === id ? { ...it, visible: !it.visible } : it)),
      );
    },
    [items, persist],
  );

  const move = useCallback(
    (id: string, direction: -1 | 1) => {
      const idx = items.findIndex((it) => it.id === id);
      const target = idx + direction;
      if (idx === -1 || target < 0 || target >= items.length) return;
      const next = [...items];
      [next[idx], next[target]] = [next[target], next[idx]];
      persist(next);
    },
    [items, persist],
  );

  const reset = useCallback(() => {
    persist(defaultNavItems());
  }, [persist]);

  const destinations = useMemo(
    () =>
      items
        .filter((it) => it.visible)
        .map((it) => navDestinationById(it.id))
        .filter((d): d is NavDestination => Boolean(d)),
    [items],
  );

  const isDefault = useMemo(() => {
    const base = defaultNavItems();
    if (items.length !== base.length) return false;
    return items.every(
      (it, i) => it.id === base[i].id && it.visible === base[i].visible,
    );
  }, [items]);

  return {
    items,
    destinations,
    setItems: persist,
    toggle,
    move,
    reset,
    isDefault,
  };
}

/**
 * Non-hook read for code that needs the studio's nav outside React
 * (route guards, one-shot checks).  Same healing as the hook.
 */
export function readNavDestinations(): NavDestination[] {
  return read()
    .filter((it) => it.visible)
    .map((it) => navDestinationById(it.id))
    .filter((d): d is NavDestination => Boolean(d));
}

/** Re-export so consumers don't need two imports to render a nav. */
export { NAV_DESTINATIONS };
